/**
 * <et-sfs-product> — bundle product block for the Slice Fix System landing page.
 *
 * Responsibilities
 *  - Gallery: thumbnail list + prev/next, and swapping to a variant's featured media.
 *  - Variant picker: resolves the selected variant from the option pills using the
 *    variant data serialised by Liquid, then updates price, badges, the selected
 *    option label, the add-to-cart state and the hidden variant input.
 *  - Offer countdown: ticks down to the configured deadline, rolling forward when
 *    the block runs on an evergreen cycle.
 *  - Bundle item prices are handled by et-sfs-money.js, which converts the amounts
 *    typed into the bundle metaobjects into the market's currency.
 *  - Add to cart: posts the main variant plus every bundle product line in one
 *    /cart/add request, then refreshes the theme's cart sections and opens the
 *    cart drawer — the same end state as adding from a product page.
 */
if (!customElements.get('et-sfs-product')) {
  class EtSfsProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      this.variants = this.readJSON('[data-et-variants]') || [];
      this.bundleItems = this.readJSON('[data-et-bundle-items]') || [];
      this.strings = this.readJSON('[data-et-strings]') || {};
      this.cartProperties = this.readJSON('[data-et-cart-properties]') || {};

      this.initGallery();
      this.initOptions();
      this.initForm();
      this.initCountdowns();

      this.updateVariant();
    }

    disconnectedCallback() {
      if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    }

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        console.error('[et-sfs-product] invalid JSON in', selector, error);
        return null;
      }
    }

    /* ---------------- Gallery ---------------- */

    initGallery() {
      this.slides = Array.from(this.querySelectorAll('[data-et-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-et-thumb]'));
      this.prevButton = this.querySelector('[data-et-gallery-prev]');
      this.nextButton = this.querySelector('[data-et-gallery-next]');
      this.slideIndex = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = this.slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.mediaId);
          if (index > -1) this.goToSlide(index);
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goToSlide(this.slideIndex - 1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goToSlide(this.slideIndex + 1));
      }

      this.querySelectorAll('[data-et-gallery-dot]').forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      this.initSwipe();

      this.goToSlide(0);

      // Hidden slides sit behind the active one, so their lazy images may never
      // intersect; fetch them once the browser is idle to avoid a white flash.
      const warmAll = () => this.warmSlideImages(0, this.slides.length - 1);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmAll, { timeout: 3000 });
      } else {
        window.setTimeout(warmAll, 1500);
      }
    }

    warmSlideImages(from, to) {
      for (let i = Math.max(0, from); i <= Math.min(this.slides.length - 1, to); i++) {
        this.slides[i].querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }
    }

    // Mobile hides the thumbs and arrows, so the stage itself must respond to swipes
    initSwipe() {
      const stage = this.slides.length ? this.slides[0].parentElement : null;
      if (!stage) return;

      let startX = null;
      let startY = null;

      stage.addEventListener('dragstart', (event) => event.preventDefault());

      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      });

      stage.addEventListener('pointercancel', () => {
        startX = null;
        startY = null;
      });

      stage.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        startX = null;
        startY = null;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.goToSlide(this.slideIndex + (deltaX < 0 ? 1 : -1));
      });
    }

    goToSlide(index) {
      if (!this.slides.length) return;

      this.slideIndex = Math.min(this.slides.length - 1, Math.max(0, index));

      // The idle warm-up may not have run yet — make sure the shown slide and its
      // neighbours are fetching before the fade reveals them.
      this.warmSlideImages(this.slideIndex - 1, this.slideIndex + 1);

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.slideIndex);
      });

      const activeId = this.slides[this.slideIndex].dataset.mediaId;
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId === activeId);
        thumb.setAttribute('aria-current', thumb.dataset.mediaId === activeId ? 'true' : 'false');
      });

      if (this.prevButton) this.prevButton.disabled = this.slideIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.slideIndex === this.slides.length - 1;

      const dots = this.querySelectorAll('[data-et-gallery-dot]');
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.slideIndex));
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goToSlide(index);
    }

    /* ---------------- Variant picker ---------------- */

    initOptions() {
      this.optionInputs = Array.from(this.querySelectorAll('[data-et-option-input], [data-et-option-select]'));
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.updateVariant());
      });
    }

    optionGroups() {
      return Array.from(this.querySelectorAll('[data-et-option-group]'));
    }

    // A group is either pills or, where an option has many values, a dropdown
    selectedOptions() {
      return this.optionGroups().map((group) => {
        const select = group.querySelector('[data-et-option-select]');
        if (select) return select.value;

        const checked = group.querySelector('[data-et-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    matchVariant(selection) {
      return this.variants.find((variant) =>
        variant.options.every(
          (value, i) => selection[i] === undefined || selection[i] === null || value === selection[i]
        )
      );
    }

    updateVariant() {
      const selected = this.selectedOptions();
      this.currentVariant = selected.length ? this.matchVariant(selected) : this.variants[0];

      this.updateSelectedLabels(selected);
      this.updateAvailabilityFlags(selected);
      this.updatePrice();
      this.updateBadges();
      this.updateAddToCart();

      if (this.currentVariant) {
        const input = this.querySelector('[data-et-main-variant]');
        if (input) input.value = this.currentVariant.id;
        this.showMedia(this.currentVariant.featured_media_id);
      }

      // Satellites (the sticky bar) mirror price, badges and option state from here
      this.dispatchEvent(
        new CustomEvent('et-sfs:variant-change', {
          bubbles: true,
          detail: { variant: this.currentVariant || null }
        })
      );
    }

    // "Swing Sync Size: REGULAR" — the chosen value is echoed next to the label
    updateSelectedLabels(selected) {
      this.optionGroups().forEach((group, index) => {
        const target = group.querySelector('[data-et-option-selected]');
        if (target) target.textContent = selected[index] || '';
      });
    }

    // Mark option values that produce no purchasable variant given the other picks
    updateAvailabilityFlags(selected) {
      this.optionGroups().forEach((group, groupIndex) => {
        group.querySelectorAll('[data-et-option-input]').forEach((input) => {
          const probe = selected.slice();
          probe[groupIndex] = input.value;
          const match = this.matchVariant(probe);
          const soldOut = !match || !match.available;
          input.dataset.available = soldOut ? 'false' : 'true';
          const label = group.querySelector(`[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', soldOut);
        });
      });
    }

    updatePrice() {
      if (!this.currentVariant) return;

      const priceEl = this.querySelector('[data-et-price]');
      if (priceEl) priceEl.innerHTML = this.currentVariant.price;

      const compareEl = this.querySelector('[data-et-compare]');
      if (compareEl) {
        if (this.currentVariant.compare_display) {
          compareEl.innerHTML = this.currentVariant.compare_display;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      // The main product row in "What's included" mirrors the selected variant
      const includedPrice = this.querySelector('[data-et-included-price]');
      if (includedPrice) includedPrice.innerHTML = this.currentVariant.price;
    }

    /*
      Badge labels double as templates: [percent] and [amount] are replaced with
      the saving for the selected variant. A badge whose template needs a value
      that does not exist stays hidden.
    */
    updateBadges() {
      const variant = this.currentVariant;

      this.querySelectorAll('[data-et-badge]').forEach((badge) => {
        const template = badge.dataset.template || '';
        if (!template) {
          badge.hidden = true;
          return;
        }

        const needsPercent = template.indexOf('[percent]') > -1;
        const needsAmount = template.indexOf('[amount]') > -1;

        if (!variant || (needsPercent && !variant.savings_percent) || (needsAmount && !variant.savings_amount)) {
          badge.hidden = true;
          return;
        }

        badge.innerHTML = template
          .replace('[percent]', variant.savings_percent)
          .replace('[amount]', variant.savings_amount);
        badge.hidden = false;
      });
    }

    updateAddToCart() {
      const button = this.querySelector('[data-et-atc]');
      if (!button) return;

      const textEl = button.querySelector('[data-et-atc-text]');
      const available = this.currentVariant && this.currentVariant.available;

      button.disabled = !available;
      button.classList.toggle('is-sold-out', !available);

      if (!textEl) return;
      if (!this.currentVariant) {
        textEl.textContent = this.strings.unavailable || '';
      } else if (!available) {
        textEl.textContent = this.strings.sold_out || '';
      } else {
        textEl.textContent = this.strings.add_to_cart || '';
      }
    }

    /* ---------------- Offer countdown ---------------- */

    initCountdowns() {
      this.countdowns = Array.from(this.querySelectorAll('[data-et-countdown]'));
      if (!this.countdowns.length) return;

      this.tickCountdowns();
      this.countdownTimer = window.setInterval(() => this.tickCountdowns(), 1000);
    }

    tickCountdowns() {
      const now = Date.now();
      const pad = (n) => String(n).padStart(2, '0');

      this.countdowns.forEach((el) => {
        let endsAt = parseInt(el.dataset.endsAt, 10);
        const cycle = parseInt(el.dataset.cycle, 10) || 0;
        if (!endsAt) return;

        // Evergreen timers roll their deadline forward instead of hitting zero
        if (endsAt <= now && cycle > 0) {
          while (endsAt <= now) endsAt += cycle;
          el.dataset.endsAt = endsAt;
        }

        const totalSeconds = Math.floor(Math.max(0, endsAt - now) / 1000);
        const values = {
          hours: pad(Math.floor(totalSeconds / 3600)),
          minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
          seconds: pad(totalSeconds % 60)
        };

        Object.keys(values).forEach((unit) => {
          const target = el.querySelector(`[data-unit="${unit}"]`);
          if (target) target.textContent = values[unit];
        });
      });
    }

    /* ---------------- Add to cart ---------------- */

    initForm() {
      this.form = this.querySelector('[data-et-form]');
      if (!this.form) return;
      this.errorEl = this.querySelector('[data-et-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    /**
     * Line item property for a role, so discount logic can tell the paid product
     * apart from the free bundle products. Returns null when unconfigured.
     *
     * @param {'main'|'bundle'} role
     */
    lineProperties(role) {
      const name = this.cartProperties[role];
      if (!name) return null;

      const properties = {};
      properties[name] = this.cartProperties.value || 'true';
      return properties;
    }

    cartSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    onSubmit(event) {
      event.preventDefault();
      this.addToCart(this.querySelector('[data-et-atc]'));
    }

    /**
     * Public entry point — adds the selected variant plus every bundle line.
     * Other sections (e.g. the trust banner CTA) call this so there is a single
     * add-to-cart path on the page.
     *
     * @param {HTMLElement} [trigger] button to show the loading state on
     * @returns {Promise<{ok: boolean, message: string}>}
     */
    addToCart(trigger) {
      if (!this.currentVariant || !this.currentVariant.available) {
        const message = this.currentVariant ? this.strings.sold_out : this.strings.unavailable;
        this.showError(message || '');
        return Promise.resolve({ ok: false, message: message || '' });
      }

      if (trigger) {
        if (trigger.classList.contains('loading')) {
          return Promise.resolve({ ok: false, message: '' });
        }
        trigger.classList.add('loading');
        trigger.setAttribute('aria-disabled', 'true');
      }

      this.showError('');

      const mainLine = { id: this.currentVariant.id, quantity: 1 };
      const mainProperties = this.lineProperties('main');
      if (mainProperties) mainLine.properties = mainProperties;

      const bundleProperties = this.lineProperties('bundle');

      const items = [mainLine].concat(
        this.bundleItems.map((item) => {
          const line = { id: item.id, quantity: item.quantity || 1 };
          if (bundleProperties) line.properties = bundleProperties;
          return line;
        })
      );

      const sections = this.cartSectionsToRender().map((section) => section.section);
      const addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';

      return fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const message = response.description || response.message || '';
            this.showError(message);
            return { ok: false, message: message };
          }

          this.renderCartSections(response.sections);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: response.items ? response.items[0] : response
            });
          }

          this.openCartDrawer();
          return { ok: true, message: '' };
        })
        .catch((error) => {
          console.error('[et-sfs-product] add to cart failed', error);
          const message = this.strings.cart_error || '';
          this.showError(message);
          return { ok: false, message: message };
        })
        .finally(() => {
          if (trigger) {
            trigger.classList.remove('loading');
            trigger.removeAttribute('aria-disabled');
          }
        });
    }

    renderCartSections(sections) {
      if (!sections) return;

      this.cartSectionsToRender().forEach((section) => {
        const host = document.getElementById(section.id);
        if (!host || !sections[section.section]) return;

        const target = host.querySelector(section.selector) || host;
        const parsed = new DOMParser().parseFromString(sections[section.section], 'text/html');
        const source = parsed.querySelector(section.selector) || parsed.querySelector('.shopify-section');

        if (source) target.innerHTML = source.innerHTML;
      });
    }

    openCartDrawer() {
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof OverlayManager !== 'undefined') {
        OverlayManager.open(cartDrawer);
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart-drawer:open');
        }
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('et-sfs-product', EtSfsProduct);
}
